import { Sheet, SheetContent, SheetTitle } from '@goaltree/ui/components/sheet'
import { cn } from '@goaltree/ui/lib/utils'
import {
  BrickWall,
  CalendarX,
  Check,
  Clock,
  Hand,
  type LucideIcon,
  Moon,
  SkipForward,
  Sofa,
  Thermometer,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

// One-tap skip reasons (never required). Keys map to i18n labels under today.skipReason;
// habits get their own set since "too big" rarely fits a 10-minute habit.
export const TASK_SKIP_REASONS = ['tooBig', 'noTime', 'notNow', 'wrongDay'] as const
export const HABIT_SKIP_REASONS = ['noTime', 'tired', 'restDay', 'unwell'] as const

const SKIP_REASON_ICON: Record<string, LucideIcon> = {
  tooBig: BrickWall,
  noTime: Clock,
  notNow: Hand,
  wrongDay: CalendarX,
  tired: Moon,
  restDay: Sofa,
  unwell: Thermometer,
}

export function SkipReasonIcon({ reason, className }: { reason: string; className?: string }) {
  const Icon = SKIP_REASON_ICON[reason]
  return Icon ? <Icon aria-hidden className={cn('size-3.5 shrink-0', className)} /> : null
}

function SkipReasonToast({
  id,
  title,
  reasons,
  onReason,
  onUndo,
}: {
  id: string | number
  title: string
  reasons: readonly string[]
  onReason: (reason: string) => void
  onUndo?: () => void
}) {
  const { t } = useTranslation()
  const [picked, setPicked] = useState<string | null>(null)
  return (
    <div className="mx-auto flex w-[min(340px,calc(100vw-32px))] flex-col gap-2.5 rounded-2xl border border-border bg-popover px-3.5 py-3 text-popover-foreground shadow-xl">
      <div className="flex items-center gap-2.5">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <SkipForward className="size-3.5" strokeWidth={2.5} />
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">
          {t('today.skippedTitle', { title })}
        </span>
        {onUndo && (
          <button
            type="button"
            onClick={() => {
              onUndo()
              toast.dismiss(id)
            }}
            className="shrink-0 cursor-pointer rounded-full px-2.5 py-1 text-[12.5px] font-bold text-primary-text transition-colors hover:bg-primary/10"
          >
            {t('common.undo', { defaultValue: 'Undo' })}
          </button>
        )}
      </div>
      {picked ? (
        <p className="flex items-center gap-1.5 pl-[34px] text-[12px] text-muted-foreground">
          <Check className="size-3.5 text-primary-text" strokeWidth={3} />
          {t('today.skipReasonThanks')}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5 pl-[34px]">
          <span className="mr-0.5 text-[11.5px] text-muted-foreground">
            {t('today.skipReasonPrompt')}
          </span>
          {reasons.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => {
                onReason(r)
                setPicked(r)
                setTimeout(() => toast.dismiss(id), 1200)
              }}
              className={cn(
                'flex cursor-pointer items-center gap-1 rounded-full bg-muted/60 px-2.5 py-1 text-[12px] font-medium transition-colors hover:bg-muted',
              )}
            >
              <SkipReasonIcon reason={r} />
              {t(`today.skipReason.${r}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// After a swipe-skip (no reason captured yet): a compact toast with Undo and the
// optional one-tap reason, which patches the just-created skip record.
export function offerSkipReason(opts: {
  title: string
  reasons: readonly string[]
  onReason: (reason: string) => void
  onUndo?: () => void
}) {
  toast.custom((id) => <SkipReasonToast id={id} {...opts} />, {
    duration: 6000,
    unstyled: true,
    className: '!w-full !bg-transparent !border-0 !shadow-none !p-0',
    style: { height: 'auto' },
  })
}

// Habit swipe-left / "Skip today": pick the reason up front (or none), then skip.
export function HabitSkipSheet({
  open,
  onOpenChange,
  title,
  onSkip,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  onSkip: (reason?: string) => void
}) {
  const { t } = useTranslation()
  const pick = (reason?: string) => () => {
    onOpenChange(false)
    onSkip(reason)
  }
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="rounded-t-2xl px-3 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] sm:bottom-4 sm:mx-auto sm:max-w-[420px] sm:rounded-2xl sm:border"
      >
        <SheetTitle className="px-3 pb-0.5 text-[15px] font-bold">
          {t('today.skipHabitTitle')}
        </SheetTitle>
        <p className="truncate px-3 pb-2 text-[13px] text-muted-foreground">{title}</p>
        {HABIT_SKIP_REASONS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={pick(r)}
            className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-3 text-left text-[15px] font-semibold transition-colors hover:bg-muted"
          >
            <SkipReasonIcon reason={r} className="size-5 w-6" />
            {t(`today.skipReason.${r}`)}
          </button>
        ))}
        <button
          type="button"
          onClick={pick()}
          className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-3 text-left text-[15px] font-semibold text-muted-foreground transition-colors hover:bg-muted"
        >
          <SkipForward className="mx-0.5 size-5" />
          {t('today.skipNoReason')}
        </button>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="mt-1 w-full cursor-pointer rounded-xl bg-muted/60 py-3 text-[15px] font-semibold transition-colors hover:bg-muted"
        >
          {t('common.cancel')}
        </button>
      </SheetContent>
    </Sheet>
  )
}
